import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { colors } from '../theme/colors';
import { useAuth } from '../context/AuthContext';
import { createLinkedAccount, ensureParentAccountExists } from '../utils/coaInitializer';
import { getAreasRegionsCached, upsertLocalMaster, invalidateMaster } from '../utils/masterDataStore';

/**
 * QuickAddCustomerModal — "create a customer on the fly" dialog for sales forms
 * (Quotation). Captures the name, phone and the grouping fields this system
 * actually has for a customer: Region → Area, plus Customer Class when the
 * price-tier module is on. Everything else (credit limit, NTN, salesperson,
 * pricing rows…) keeps the same defaults the full master uses and can be filled
 * in later under Master → Customers.
 *
 * It deliberately performs the SAME create chain as the customer master:
 * ensure the parent COA account exists → write the customer → create its linked
 * receivable account → stamp the account id back on the customer. Skipping the
 * linked account would produce a customer that looks fine in the picker but
 * cannot post an invoice, so the account failing is treated as a hard error
 * here rather than a warning.
 *
 * On success it calls onCreated(newCustomer) with the full row (incl. the new
 * id) so the caller can select it straight into the form.
 */
const QuickAddCustomerModal = ({ companyId, onCreated, onClose }) => {
  const { userProfile, currentUser, isModuleEnabled } = useAuth();
  const classEnabled = typeof isModuleEnabled === 'function' ? isModuleEnabled('customer_class') : false;

  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [regionId, setRegionId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [customerClass, setCustomerClass] = useState('');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [saving, setSaving] = useState(false);

  // Regions + areas come from the shared session cache, so opening this dialog
  // repeatedly costs nothing after the first time.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!companyId) return;
      try {
        const { areas: a, regions: r } = await getAreasRegionsCached(companyId);
        if (cancelled) return;
        setRegions([...(r || [])].sort((x, y) => (x.regionName || '').localeCompare(y.regionName || '')));
        setAreas(a || []);
      } catch (e) { console.error('Load regions/areas failed:', e); }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Areas belong to a region — same rule as the customer master, where the Area
  // picker stays disabled until a Region is chosen.
  const areasForRegion = regionId ? areas.filter(a => a.regionId === regionId) : [];

  const handleSave = async () => {
    const name = companyName.trim();
    if (!name) { alert('Customer name is required'); return; }
    if (!companyId) return;
    const uid = userProfile?.uid || currentUser?.uid;
    if (!uid) { alert('Not signed in properly — please reload and try again.'); return; }

    setSaving(true);
    try {
      // Same guard the master uses: without a parent receivable group the
      // linked account cannot be created, so stop before writing anything.
      const parentCheck = await ensureParentAccountExists(companyId, 'customer', uid);
      if (!parentCheck.success) {
        alert(`Cannot create customer: ${parentCheck.message}. Please ensure Chart of Accounts is properly initialized.`);
        setSaving(false);
        return;
      }

      // Full default shape, matching the customer master, so nothing downstream
      // (aging, credit checks, pricing, prints) meets a missing field.
      const customerData = {
        companyName: name,
        contactPerson: '',
        email: '',
        phone: phone.trim(),
        ntn: '',
        strn: '',
        address: '',
        regionId: regionId || '',
        areaId: areaId || '',
        postalCode: '',
        country: 'Pakistan',
        creditLimit: 0,
        creditDays: 30,
        isActive: true,
        notes: '',
        pricingExclusive: false,
        salesPersonId: '',
        salesPersonName: '',
        defaultDiscountPercent: null,
        defaultTaxRate: null,
        customerClass: classEnabled ? (customerClass || null) : null,
        createdAt: new Date().toISOString(),
        createdBy: uid,
        updatedAt: new Date().toISOString(),
      };

      const customersRef = collection(db, 'companies', companyId, 'customers');
      const docRef = await addDoc(customersRef, customerData);

      // The linked receivable account is what makes the customer postable. If it
      // fails the customer row still exists, so say so plainly and point at the
      // master rather than silently handing back a half-built customer.
      let linked = {};
      try {
        const accountResult = await createLinkedAccount(companyId, 'customer', docRef.id, name, uid);
        if (accountResult.success) {
          linked = {
            linkedAccountId: accountResult.accountId,
            linkedAccountCode: accountResult.accountCode,
          };
          await updateDoc(doc(customersRef, docRef.id), linked);
        } else {
          alert('Customer created, but its ledger account could not be created. Open Master → Customers and edit it before invoicing.');
        }
      } catch (accountError) {
        console.error('Error creating linked account:', accountError);
        alert('Customer created, but its ledger account could not be created. Open Master → Customers and edit it before invoicing.');
      }

      // Write-through to this device's cache so the customer is pickable on the
      // form immediately — invalidateMaster() below re-downloads the whole list
      // in the background, which a user typing the next field would beat.
      const row = { id: docRef.id, ...customerData, ...linked };
      await upsertLocalMaster(companyId, 'customers', row);
      invalidateMaster(companyId, 'customers');
      invalidateMaster(companyId, 'accounts');

      onCreated?.(row);
    } catch (e) {
      console.error('Quick add customer failed:', e);
      alert('Failed to add customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <h3>Quick Add Customer</h3>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        <Body>
          <Field>
            <label>Customer Name *</label>
            <input
              autoFocus
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="e.g. Khokhar Enterprises"
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            />
          </Field>
          <Row>
            <Field>
              <label>Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0300-1234567" />
            </Field>
            {classEnabled && (
              <Field>
                <label>Customer Class</label>
                <select value={customerClass} onChange={e => setCustomerClass(e.target.value)}>
                  <option value="">— None (Retail price) —</option>
                  <option value="Retail">Retail</option>
                  <option value="Wholesale">Wholesale</option>
                  <option value="Dealer">Dealer</option>
                </select>
              </Field>
            )}
          </Row>
          <Row>
            <Field>
              <label>Region</label>
              <select
                value={regionId}
                onChange={e => { setRegionId(e.target.value); setAreaId(''); }}
              >
                <option value="">Select Region</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.regionName}</option>)}
              </select>
            </Field>
            <Field>
              <label>Area</label>
              <select value={areaId} onChange={e => setAreaId(e.target.value)} disabled={!regionId}>
                <option value="">{regionId ? 'Select Area' : 'Pick a region first'}</option>
                {areasForRegion.map(a => <option key={a.id} value={a.id}>{a.areaName}</option>)}
              </select>
            </Field>
          </Row>
          <Hint>A ledger account is created automatically. Credit limit, NTN and the rest can be filled in later under Master → Customers.</Hint>
        </Body>
        <Footer>
          <CancelBtn onClick={onClose} disabled={saving}>Cancel</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={saving}>{saving ? 'Adding…' : 'Add Customer'}</SaveBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default QuickAddCustomerModal;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 3000;
`;
const Modal = styled.div`
  background: #fff; border-radius: 10px; width: 460px; max-width: 94vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.25); overflow: hidden;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #eee;
  h3 { margin: 0; font-size: 16px; color: ${colors.maroon || '#800020'}; }
`;
const CloseBtn = styled.button`
  border: none; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: #888;
`;
const Body = styled.div`padding: 18px;`;
const Row = styled.div`display: flex; gap: 12px; & > * { flex: 1; }`;
const Field = styled.div`
  margin-bottom: 12px; display: flex; flex-direction: column;
  label { font-size: 12px; color: #555; margin-bottom: 4px; }
  input, select {
    padding: 8px 10px; border: 1px solid #ccc; border-radius: 6px; font-size: 14px;
    &:disabled { background: #f4f4f4; color: #888; }
  }
`;
const Hint = styled.div`font-size: 11px; color: #999; margin-top: 4px;`;
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; padding: 14px 18px; border-top: 1px solid #eee;
`;
const CancelBtn = styled.button`
  padding: 8px 16px; border: 1px solid #ccc; background: #fff; border-radius: 6px; cursor: pointer;
`;
const SaveBtn = styled.button`
  padding: 8px 18px; border: none; background: ${colors.maroon || '#800020'}; color: #fff;
  border-radius: 6px; cursor: pointer; font-weight: 600;
  &:disabled { opacity: 0.6; cursor: default; }
`;
